// Featured Content Manager Component
// Manages featured content for all three learn sections
// Shows FeaturedContentGrid with admin controls for adding/removing items

import React, { useState, useEffect, useCallback } from 'react';
import { FeaturedContentGrid } from './FeaturedContentGrid';
import { VideoSearchBar } from './VideoSearchBar';
import { QASearchBar } from './QASearchBar';
import { TriviaSearchBar } from './TriviaSearchBar';
import { logger } from '../../../core';
import './FeaturedContentManager.css';

/** Batch APIs use $in and do not preserve featured order — align UI to stored `items` order. */
function sortRecordsByFeaturedOrder(records, featuredItems, getId = (r) => String(r.id)) {
  if (!records?.length || !featuredItems?.length) return records || [];
  const order = new Map(featuredItems.map((entry, i) => [String(entry.id), i]));
  return [...records].sort((a, b) => {
    const ai = order.has(getId(a)) ? order.get(getId(a)) : 9999;
    const bi = order.has(getId(b)) ? order.get(getId(b)) : 9999;
    return ai - bi;
  });
}

export function FeaturedContentManager() {
  const [featuredData, setFeaturedData] = useState({
    "tutorial-videos": { items: [] },
    "questions-answers": { items: [] },
    "trivia": { items: [] }
  });
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [featuredQA, setFeaturedQA] = useState([]);
  const [featuredTrivia, setFeaturedTrivia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadFeaturedVideos = useCallback(async (featuredItems) => {
    if (!featuredItems || featuredItems.length === 0) {
      setFeaturedVideos([]);
      return;
    }

    try {
      const ids = featuredItems.map(item => item.id);
      const response = await fetch(`/api/learn/videos/batch?ids=${ids.join(',')}`);
      const data = await response.json();

      if (data.success && data.videos) {
        setFeaturedVideos(sortRecordsByFeaturedOrder(data.videos, featuredItems));
      } else {
        setFeaturedVideos([]);
        logger.error({
          tags: 'admin|featured|error',
          color1: 'red',
        }, 'Failed to load featured videos:', data.error);
      }
    } catch (error) {
      setFeaturedVideos([]);
      logger.error({
        tags: 'admin|featured|error',
        color1: 'red',
      }, 'Error loading featured videos:', error);
    }
  }, []);

  const loadFeaturedQA = useCallback(async (featuredItems) => {
    if (!featuredItems || featuredItems.length === 0) {
      setFeaturedQA([]);
      return;
    }

    try {
      const ids = featuredItems.map(item => item.id);
      const response = await fetch(`/api/learn/questions/batch?ids=${ids.join(',')}`);
      const data = await response.json();

      if (data.success && data.qa) {
        // Map API response to match component expectations (likes -> helpful)
        const mappedQA = sortRecordsByFeaturedOrder(
          data.qa.map(qa => ({
            ...qa,
            helpful: qa.likes || 0,
          })),
          featuredItems
        );
        setFeaturedQA(mappedQA);
      } else {
        setFeaturedQA([]);
        logger.error({
          tags: 'admin|featured|error',
          color1: 'red',
        }, 'Failed to load featured Q&A:', data.error);
      }
    } catch (error) {
      setFeaturedQA([]);
      logger.error({
        tags: 'admin|featured|error',
        color1: 'red',
      }, 'Error loading featured Q&A:', error);
    }
  }, []);

  const loadFeaturedTrivia = useCallback(async (featuredItems) => {
    if (!featuredItems || featuredItems.length === 0) {
      setFeaturedTrivia([]);
      return;
    }

    try {
      // Load trivia items individually since there's no batch endpoint
      const triviaPromises = featuredItems.map(async (item) => {
        const response = await fetch(`/api/learn/trivia/${item.id}`);
        const data = await response.json();
        return data.success ? data.trivia : null;
      });

      const triviaResults = await Promise.all(triviaPromises);
      const validTrivia = triviaResults.filter(trivia => trivia !== null);

      setFeaturedTrivia(validTrivia);
    } catch (error) {
      setFeaturedTrivia([]);
      logger.error({
        tags: 'admin|featured|error',
        color1: 'red',
      }, 'Error loading featured trivia:', error);
    }
  }, []);

  const loadFeaturedData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/learn/admin/featured', {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to load featured content');
      }

      setFeaturedData(data.featured);

      // Load the actual data for featured items
      await Promise.all([
        loadFeaturedVideos(data.featured["tutorial-videos"]?.items || []),
        loadFeaturedQA(data.featured["questions-answers"]?.items || []),
        loadFeaturedTrivia(data.featured["trivia"]?.items || [])
      ]);

      logger.log({
        tags: 'admin|featured|load',
        color1: 'blue',
      }, 'Loaded featured content data');
    } catch (error) {
      logger.error({
        tags: 'admin|featured|load|error',
        color1: 'red',
      }, 'Failed to load featured content:', error);

      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [loadFeaturedVideos, loadFeaturedQA, loadFeaturedTrivia]);

  // Load featured data on component mount
  useEffect(() => {
    loadFeaturedData();
  }, [loadFeaturedData]);

  const updateSectionItems = async (sectionId, items) => {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`/api/learn/admin/featured/${sectionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update featured content');
      }

      // Update local state
      setFeaturedData(prev => ({
        ...prev,
        [sectionId]: {
          ...prev[sectionId],
          items: items
        }
      }));

      // Reload data for the updated section
      if (sectionId === "tutorial-videos") {
        await loadFeaturedVideos(items);
      } else if (sectionId === "questions-answers") {
        await loadFeaturedQA(items);
      } else if (sectionId === "trivia") {
        await loadFeaturedTrivia(items);
      }

      logger.log({
        tags: 'admin|featured|update',
        color1: 'green',
      }, `Updated featured content for ${sectionId}: ${items.length} items`);
    } catch (error) {
      logger.error({
        tags: 'admin|featured|update|error',
        color1: 'red',
      }, `Failed to update featured content for ${sectionId}:`, error);

      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const reorderSectionItem = async (sectionId, itemId, direction) => {
    const items = featuredData[sectionId]?.items;
    if (!items?.length) return;
    const list = [...items];
    const idx = list.findIndex(item => String(item.id) === String(itemId));
    if (idx < 0) return;
    const j = idx + direction;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    await updateSectionItems(sectionId, list);
  };

  const handleDeleteVideo = async (videoId) => {
    if (window.confirm('Remove this video from featured content?')) {
      await updateSectionItems("tutorial-videos",
        featuredData["tutorial-videos"].items.filter(item => item.id !== videoId)
      );
    }
  };

  const handleDeleteQA = async (qaId) => {
    if (window.confirm('Remove this Q&A from featured content?')) {
      await updateSectionItems("questions-answers",
        featuredData["questions-answers"].items.filter(item => item.id !== qaId)
      );
    }
  };

  const handleDeleteTrivia = async (triviaId) => {
    if (window.confirm('Remove this trivia from featured content?')) {
      await updateSectionItems("trivia",
        featuredData["trivia"].items.filter(item => item.id !== triviaId)
      );
    }
  };

  if (loading) {
    return (
      <div className="featured-content-loading">
        <div className="loading-spinner"></div>
        <p>Loading featured content...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="featured-content-error">
        <i className="fa-solid fa-exclamation-triangle"></i>
        <h3>Error Loading Featured Content</h3>
        <p>{error}</p>
        <button onClick={loadFeaturedData} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="featured-content-manager">
      <div className="manager-header">
        <h3>Manage Featured Content</h3>
        <p>Choose which content appears in the featured section of the main Learn page.</p>
        {saving && (
          <div className="saving-indicator">
            <i className="fa-solid fa-spinner fa-spin"></i>
            <span>Saving...</span>
          </div>
        )}
      </div>

      {/* Search bars for adding content */}
      <div className="featured-search-bars">
        <div className="search-bar-section">
          <h4>Tutorial Videos</h4>
          <VideoSearchBar onAddVideo={(video) => {
            const currentItems = featuredData["tutorial-videos"]?.items || [];
            if (!currentItems.find(item => item.id === video.id)) {
              updateSectionItems("tutorial-videos", [...currentItems, {
                id: video.id,
                title: video.name || video.title
              }]);
            }
          }} />
        </div>

        <div className="search-bar-section">
          <h4>Questions & Answers</h4>
          <QASearchBar onAddQA={(qa) => {
            const currentItems = featuredData["questions-answers"]?.items || [];
            if (!currentItems.find(item => item.id === qa.id)) {
              updateSectionItems("questions-answers", [...currentItems, {
                id: qa.id,
                title: qa.question
              }]);
            }
          }} />
        </div>

        <div className="search-bar-section">
          <h4>Trivia</h4>
          <TriviaSearchBar onAddTrivia={(trivia) => {
            const currentItems = featuredData["trivia"]?.items || [];
            if (!currentItems.find(item => item.id === trivia.id)) {
              updateSectionItems("trivia", [...currentItems, {
                id: trivia.id,
                title: trivia.question
              }]);
            }
          }} />
        </div>
      </div>

      {/* Featured content grid with delete buttons */}
      <div className="featured-grid-container">
        <FeaturedContentGrid
          tutorialVideos={[]} // Empty since we're providing featured data
          questionsAnswers={[]}
          triviaQuestions={[]}
          featuredVideos={featuredVideos}
          featuredQA={featuredQA}
          featuredTrivia={featuredTrivia}
          showViewAllButtons={false}
          isAdmin={true}
          onDeleteVideo={handleDeleteVideo}
          onDeleteQA={handleDeleteQA}
          onDeleteTrivia={handleDeleteTrivia}
          onReorderFeaturedItem={reorderSectionItem}
        />
      </div>
    </div>
  );
}